import React from 'react';
import { MapPin, Activity, Clock, TrendingUp } from 'lucide-react';

const ShopperJourney: React.FC = () => {
  const journeys = [
    {
      id: 1,
      path: 'Entry → Women\'s Section → Fitting Room → Checkout',
      duration: '24m',
      interactions: 7,
      outcome: 'Purchase'
    },
    {
      id: 2,
      path: 'Entry → Men\'s Section → Exit',
      duration: '8m',
      interactions: 3,
      outcome: 'Browse Only'
    },
    {
      id: 3,
      path: 'Entry → Accessories → Women\'s Section → Checkout',
      duration: '32m',
      interactions: 12,
      outcome: 'Purchase'
    },
    {
      id: 4,
      path: 'Entry → Sale Section → Fitting Room → Checkout',
      duration: '18m',
      interactions: 5,
      outcome: 'Purchase'
    }
  ];

  const popularPaths = [
    { path: 'Entry → Women\'s → Fitting Room', count: 145 },
    { path: 'Entry → Accessories → Checkout', count: 98 },
    { path: 'Entry → Sale Section → Checkout', count: 87 },
    { path: 'Entry → Men\'s → Fitting Room', count: 76 }
  ];

  const metrics = [
    { label: 'Avg. Path Length', value: '4.2 zones', icon: MapPin },
    { label: 'Avg. Journey Time', value: '21m', icon: Clock },
    { label: 'Conversion Rate', value: '24%', icon: TrendingUp },
    { label: 'Interactions per Visit', value: '6.8', icon: Activity }
  ];

  return (
    <div className="p-6" style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--sca-ink)' }}>
          Shopper Journey Analytics
        </h1>
        <p style={{ color: 'var(--sca-muted)' }}>
          Track customer movement patterns and shopping behavior
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {metrics.map((metric, idx) => (
          <div key={idx} className="card">
            <div className="flex items-center justify-between mb-3">
              <metric.icon size={24} style={{ color: 'var(--sca-teal)' }} />
            </div>
            <div className="text-2xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
              {metric.value}
            </div>
            <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
              {metric.label}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="card">
          <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
            Popular Shopping Paths
          </h3>
          <div className="space-y-4">
            {popularPaths.map((item, idx) => (
              <div key={idx}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm" style={{ color: 'var(--sca-ink)' }}>{item.path}</span>
                  <span className="font-semibold" style={{ color: 'var(--sca-teal)' }}>
                    {item.count}
                  </span>
                </div>
                <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'var(--sca-border)' }}>
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: `${(item.count / 145) * 100}%`,
                      backgroundColor: 'var(--sca-teal)'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
            Journey Insights
          </h3>
          <div className="space-y-3">
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--sca-hover)' }}>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--sca-ink)' }}>
                High Conversion Path
              </div>
              <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>
                Entry → Sale Section → Fitting Room has 38% conversion rate
              </div>
            </div>
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--sca-hover)' }}>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--sca-ink)' }}>
                Long Dwell Zone
              </div>
              <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>
                Women's Section has avg. 14m dwell time, highest in store
              </div>
            </div>
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--sca-hover)' }}>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--sca-ink)' }}>
                Quick Exit Pattern
              </div>
              <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>
                Entry → Men's → Exit in under 10m represents 22% of visits
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
          Recent Journeys
        </h3>
        <div className="space-y-3">
          {journeys.map((journey) => (
            <div
              key={journey.id}
              className="p-4 rounded-lg"
              style={{ backgroundColor: 'var(--sca-hover)', border: '1px solid var(--sca-border)' }}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="text-sm font-medium mb-1" style={{ color: 'var(--sca-ink)' }}>
                    {journey.path}
                  </div>
                  <div className="flex gap-4 text-xs" style={{ color: 'var(--sca-muted)' }}>
                    <span>Duration: {journey.duration}</span>
                    <span>Interactions: {journey.interactions}</span>
                  </div>
                </div>
                <span
                  className="px-2 py-1 rounded text-xs"
                  style={{
                    backgroundColor: journey.outcome === 'Purchase' ? 'var(--sca-badge-bg)' : 'var(--sca-warm-chip)',
                    color: journey.outcome === 'Purchase' ? 'var(--sca-teal)' : 'var(--sca-warm-text)'
                  }}
                >
                  {journey.outcome}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ShopperJourney;
